import { Link } from "react-router-dom";
import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Mail, MessageSquare, HelpCircle, Send, MapPin, Clock, Loader2 } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: delay * 0.001, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

const FAQ = [
  { q: "How do I get started with Omni?", a: "Visit the sign-up page, create a free account, and start chatting immediately. No credit card required." },
  { q: "Is my data safe and private?", a: "Absolutely. All conversations are encrypted. We never sell, share, or use your data for advertising. You can export or delete your data at any time." },
  { q: "Can I use Omni on my phone?", a: "Yes. Omni is a Progressive Web App (PWA) that works on any device. Native iOS and Android apps are also available." },
  { q: "What languages does Omni support?", a: "Omni supports 6 languages for free and over 107 premium languages with the Global Voice Pack add-on." },
  { q: "How do Family and Team plans work?", a: "Duo, Family, and Platinum plans allow multiple members under one subscription. Each member gets their own private AI experience with age-appropriate settings." },
  { q: "Is Omni a replacement for professional therapy?", a: "No. Omni is an AI companion that provides support, but it is not a licensed therapist. In a crisis, always contact emergency services or a licensed professional." },
  { q: "How do I cancel or change my plan?", a: "Go to Settings → Subscription to upgrade, downgrade, or cancel at any time. Changes take effect immediately." },
  { q: "Can I try premium features before paying?", a: "The Free tier gives you access to core features. Many premium features like the Wisdom Council offer preview access so you can experience them before upgrading." },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch {
      toast.success("Message received! We'll get back to you soon.");
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* ═══════ HERO ═══════ */}
      <section className="relative pt-32 pb-16 md:pt-44 md:pb-20 px-6 lg:px-12" data-testid="contact-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-0 w-[500px] h-[400px]" style={{ background: "radial-gradient(circle, rgba(227,18,18,0.06) 0%, transparent 65%)" }} />
        </div>
        <div className="max-w-4xl mx-auto relative z-10">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E31212] mb-6">Contact</p>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="text-5xl sm:text-6xl font-semibold tracking-tighter leading-[1.05] mb-6"
              style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
              We'd love to <span className="text-[#E31212]">hear</span> from you
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-lg text-[#A1A5B3] max-w-xl leading-relaxed">
              Have a question, suggestion, or partnership inquiry? Our team is ready to help.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══════ CONTACT FORM + INFO ═══════ */}
      <section className="py-16 px-6 lg:px-12" data-testid="contact-form-section">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Form */}
          <div className="lg:col-span-3">
            <Reveal>
              {submitted ? (
                <div className="rounded-2xl p-10 border text-center" style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.07)" }}>
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: "rgba(52,211,153,0.1)" }}>
                    <Send className="w-7 h-7 text-emerald-500" />
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
                    Message sent!
                  </h3>
                  <p className="text-[#A1A5B3] mb-6">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                  <button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", subject: "", message: "" }); }}
                    className="text-sm text-[#E31212] hover:text-[#ff1e1e] transition-colors">
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="rounded-2xl p-8 md:p-10 border space-y-5" style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.07)" }}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-xs font-medium text-[#6E7385] uppercase tracking-wider mb-2 block">Name *</label>
                      <input data-testid="contact-name" type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                        className="w-full h-12 px-4 rounded-xl text-sm text-white placeholder-[#6E7385] focus:outline-none transition-colors"
                        style={{ background: "#111625", border: "1px solid rgba(255,255,255,0.08)" }}
                        placeholder="Your name" required />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#6E7385] uppercase tracking-wider mb-2 block">Email *</label>
                      <input data-testid="contact-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                        className="w-full h-12 px-4 rounded-xl text-sm text-white placeholder-[#6E7385] focus:outline-none transition-colors"
                        style={{ background: "#111625", border: "1px solid rgba(255,255,255,0.08)" }}
                        placeholder="you@example.com" required />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#6E7385] uppercase tracking-wider mb-2 block">Subject</label>
                    <input data-testid="contact-subject" type="text" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
                      className="w-full h-12 px-4 rounded-xl text-sm text-white placeholder-[#6E7385] focus:outline-none transition-colors"
                      style={{ background: "#111625", border: "1px solid rgba(255,255,255,0.08)" }}
                      placeholder="What's this about?" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#6E7385] uppercase tracking-wider mb-2 block">Message *</label>
                    <textarea data-testid="contact-message" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-[#6E7385] focus:outline-none transition-colors resize-none"
                      style={{ background: "#111625", border: "1px solid rgba(255,255,255,0.08)" }}
                      rows={6} placeholder="Tell us how we can help..." required />
                  </div>
                  <button data-testid="contact-submit-btn" type="submit" disabled={submitting}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-white font-medium text-sm transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50"
                    style={{ background: "#E31212", boxShadow: "0 0 20px rgba(227,18,18,0.3)" }}>
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {submitting ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </Reveal>
          </div>

          {/* Info Sidebar */}
          <div className="lg:col-span-2 space-y-6">
            <Reveal delay={100}>
              <div className="rounded-2xl p-6 border" style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.07)" }}>
                <Mail className="w-5 h-5 text-[#E31212] mb-3" strokeWidth={1.5} />
                <p className="text-sm font-medium text-white mb-1">Email</p>
                <p className="text-sm text-[#A1A5B3]">support@omnimodules.com</p>
              </div>
            </Reveal>
            <Reveal delay={200}>
              <div className="rounded-2xl p-6 border" style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.07)" }}>
                <Clock className="w-5 h-5 text-[#F5A623] mb-3" strokeWidth={1.5} />
                <p className="text-sm font-medium text-white mb-1">Response Time</p>
                <p className="text-sm text-[#A1A5B3]">We respond within 24 hours</p>
              </div>
            </Reveal>
            <Reveal delay={300}>
              <div className="rounded-2xl p-6 border" style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.07)" }}>
                <MapPin className="w-5 h-5 text-[#A78BFA] mb-3" strokeWidth={1.5} />
                <p className="text-sm font-medium text-white mb-1">Headquarters</p>
                <p className="text-sm text-[#A1A5B3]">Masters Global Inc.<br />Global Operations</p>
              </div>
            </Reveal>
            <Reveal delay={400}>
              <div className="rounded-2xl p-6 border" style={{ background: "#0A0E1A", borderColor: "rgba(255,255,255,0.07)" }}>
                <MessageSquare className="w-5 h-5 text-[#34D399] mb-3" strokeWidth={1.5} />
                <p className="text-sm font-medium text-white mb-1">In-App Support</p>
                <p className="text-sm text-[#A1A5B3]">Platinum users enjoy priority in-app support</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════ FAQ ═══════ */}
      <section className="py-28 md:py-36 px-6 lg:px-12" style={{ background: "#0A0E1A" }} data-testid="contact-faq">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="flex items-center gap-3 mb-12">
              <HelpCircle className="w-5 h-5 text-[#F5A623]" strokeWidth={1.5} />
              <h2 className="text-3xl font-semibold tracking-tight"
                style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
                Frequently asked questions
              </h2>
            </div>
          </Reveal>
          <div className="space-y-0">
            {FAQ.map((item, i) => (
              <Reveal key={i} delay={i * 30}>
                <details className="group border-b py-5" style={{ borderColor: "rgba(255,255,255,0.07)" }}>
                  <summary className="flex items-center justify-between cursor-pointer list-none">
                    <span className="text-base text-white font-medium pr-4">{item.q}</span>
                    <span className="text-[#6E7385] text-xl group-open:rotate-45 transition-transform duration-200 flex-shrink-0">+</span>
                  </summary>
                  <p className="mt-3 text-sm text-[#A1A5B3] leading-relaxed">{item.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
